import { Card, Col, Row, Space, Table, Tag, Timeline, Typography } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Icon } from '../../icons/index.js';
import {
  type SystemUpdateLogEntry,
  type SystemUpdateLogScope,
  systemUpdateLogEntries,
} from './system-update-log.js';

const scopeMeta: Record<
  SystemUpdateLogScope,
  { color: string; labelZh: string; labelEn: string }
> = {
  platform: { color: 'blue', labelZh: '平台', labelEn: 'Platform' },
  crm: { color: 'green', labelZh: 'CRM', labelEn: 'CRM' },
  erp: { color: 'purple', labelZh: 'ERP', labelEn: 'ERP' },
  ops: { color: 'orange', labelZh: '运维', labelEn: 'Ops' },
};

function formatDate(value: string, isEn: boolean) {
  const date = new Date(`${value}T00:00:00`);
  if (Number.isNaN(date.getTime())) return value;
  return date.toLocaleDateString(isEn ? 'en-US' : 'zh-CN', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  });
}

function scopeTag(scope: SystemUpdateLogScope, isEn: boolean) {
  const meta = scopeMeta[scope];
  return <Tag color={meta.color}>{isEn ? meta.labelEn : meta.labelZh}</Tag>;
}

export function UpdateLogPage() {
  const { i18n } = useTranslation();
  const isEn = i18n.language?.startsWith('en');
  const entries = useMemo(
    () =>
      [...systemUpdateLogEntries].sort(
        (a, b) => new Date(b.date).getTime() - new Date(a.date).getTime(),
      ),
    [],
  );

  const columns: ColumnsType<SystemUpdateLogEntry> = [
    {
      title: isEn ? 'Date' : '日期',
      dataIndex: 'date',
      key: 'date',
      width: 120,
      render: (value: string) => formatDate(value, isEn),
    },
    {
      title: isEn ? 'Version' : '版本',
      dataIndex: 'version',
      key: 'version',
      width: 120,
      render: (value: string) => <Typography.Text code>{value}</Typography.Text>,
    },
    {
      title: isEn ? 'Scope' : '范围',
      dataIndex: 'scope',
      key: 'scope',
      width: 100,
      render: (value: SystemUpdateLogScope) => scopeTag(value, isEn),
    },
    {
      title: isEn ? 'Publisher' : '发布人',
      key: 'publisher',
      width: 120,
      render: (_, row) => isEn ? row.publisherEn : row.publisherZh,
    },
    {
      title: isEn ? 'Update' : '更新内容',
      key: 'content',
      render: (_, row) => (
        <Space direction="vertical" size={4}>
          <Typography.Text strong>{isEn ? row.titleEn : row.titleZh}</Typography.Text>
          <Typography.Text type="secondary">{isEn ? row.summaryEn : row.summaryZh}</Typography.Text>
          <Space size={[4, 4]} wrap>
            {(isEn ? row.changesEn : row.changesZh).map((item) => (
              <Tag key={item}>{item}</Tag>
            ))}
          </Space>
        </Space>
      ),
    },
  ];

  return (
    <Space direction="vertical" size={16} style={{ width: '100%' }}>
      <Card>
        <Row gutter={[16, 16]} align="middle">
          <Col flex="auto">
            <Space align="start" size={12}>
              <Icon.History size={22} />
              <Space direction="vertical" size={4}>
                <Typography.Title level={4} style={{ margin: 0 }}>
                  {isEn ? 'Update Log' : '更新日志'}
                </Typography.Title>
                <Typography.Text type="secondary">
                  {isEn
                    ? 'Track user-visible CREST, CRM, ERP, and operations changes in one place.'
                    : '集中记录 CREST、CRM、ERP 和运维侧用户可感知的系统更新。'}
                </Typography.Text>
              </Space>
            </Space>
          </Col>
          <Col>
            <Tag color="blue">
              {isEn ? `${entries.length} entries` : `共 ${entries.length} 条`}
            </Tag>
          </Col>
        </Row>
      </Card>

      <Row gutter={[16, 16]}>
        <Col xs={24} xl={9}>
          <Card title={isEn ? 'Timeline' : '更新时间线'}>
            <Timeline
              items={entries.map((entry) => ({
                color: scopeMeta[entry.scope].color,
                children: (
                  <Space direction="vertical" size={4}>
                    <Space size={8} wrap>
                      <Typography.Text strong>{entry.version}</Typography.Text>
                      {scopeTag(entry.scope, isEn)}
                      <Typography.Text type="secondary">
                        {formatDate(entry.date, isEn)}
                      </Typography.Text>
                    </Space>
                    <Typography.Text>{isEn ? entry.titleEn : entry.titleZh}</Typography.Text>
                    <Typography.Text type="secondary">
                      {isEn ? entry.summaryEn : entry.summaryZh}
                    </Typography.Text>
                  </Space>
                ),
              }))}
            />
          </Card>
        </Col>
        <Col xs={24} xl={15}>
          <Card title={isEn ? 'Details' : '更新明细'}>
            <Table
              rowKey="id"
              columns={columns}
              dataSource={entries}
              pagination={false}
              scroll={{ x: 'max-content' }}
            />
          </Card>
        </Col>
      </Row>
    </Space>
  );
}
